import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import feesHero from "@/assets/fees-hero.jpg";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FEES, FIRM, NO_CHARGE_GUARANTEE, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "How much does conveyancing cost in Queensland?",
    a: `At Coastside Law a residential sale is a fixed ${FEES.sale} including GST and a residential purchase is a fixed $1,499 including GST. Across the Queensland market legal fees for a standard residential conveyance typically run from about $800 to $2,500. On top of the legal fee you pay disbursements — searches, registration fees and, for buyers, transfer duty — which go to third parties at cost.`,
  },
  {
    q: "What is the total cost of buying a house in Queensland?",
    a: "For a buyer the legal fee is usually the smallest part. Budget for the fixed legal fee ($1,499 incl. GST with us), searches of roughly $200–$400, building and pest of about $500–$700, Titles Office registration fees, and transfer duty — which on a $700,000 home bought as your residence is the single largest figure. Sellers pay the legal fee, a small number of searches and the agent's commission.",
  },
  {
    q: "Is the fixed fee really fixed?",
    a: "Yes, for standard residential conveyancing. If something genuinely outside a standard transaction arises — a caveat, a dispute, a contract termination — we tell you before we do the work and agree the additional fee with you first.",
  },
  {
    q: "What are disbursements?",
    a: "Amounts we pay to third parties on your behalf: title and council searches, body corporate searches, Titles Office registration fees and settlement platform fees. We pass them on at cost with nothing added.",
  },
  {
    q: "Is transfer duty included in the conveyancing fee?",
    a: "No. Transfer duty is paid to the Queensland Revenue Office and depends on the price and your concession eligibility. It is usually a buyer's largest single cost. We calculate it and tell you the figure early.",
  },
  {
    q: "Do conveyancing fees differ between Brisbane, the Sunshine Coast and Moreton Bay?",
    a: "The legal work is the same across Queensland and our fixed fee does not change by suburb. What changes is the searches — a Sunshine Coast beachfront lot, a Moreton Bay unit in a body corporate scheme and a Fraser Coast acreage block each need a different search set, so disbursements vary more than fees do.",
  },
  {
    q: "Is a cheaper conveyancer worth it?",
    a: "A lower fee can be good value, but compare written quotes on the same basis. Check GST, searches, settlement platform fees and any extra charges. Queensland conveyancing is legal work performed by a law practice; ask who will handle your file and who will advise you if a contract problem arises. Price alone does not tell you the quality of the service.",
  },
  {
    q: "When do I pay?",
    a: "Generally at settlement — from the proceeds on a sale, or with the balance of the purchase price on a purchase. Some searches are paid earlier because they are ordered at the start.",
  },
];

const INCLUDED = [
  "Reviewing the contract and advising you on it",
  "Managing cooling-off, finance and building and pest dates",
  "Ordering and reviewing the standard searches for your property",
  "All correspondence with the other side, agent and your bank",
  "Preparing the settlement statement and adjustments",
  "Attending electronic settlement and confirming the result",
  "Post-settlement notifications to council and water",
];

const DISBURSEMENTS = [
  { item: "Title search and registered plan", cost: "$40 – $80", who: "Buyer and seller" },
  { item: "Council rates and water search", cost: "$70 – $180", who: "Buyer" },
  { item: "Body corporate search and disclosure", cost: "$150 – $350", who: "Units and townhouses" },
  { item: "State searches (land tax, transport, contaminated land)", cost: "$30 – $150", who: "As relevant" },
  { item: "Titles Office registration fees", cost: "From $250", who: "Buyer, scales with price" },
  { item: "Electronic settlement (PEXA) fee", cost: "$110 – $150", who: "Buyer and seller" },
  { item: "Building and pest inspection (paid direct)", cost: "$500 – $700", who: "Buyer" },
  { item: "Transfer duty", cost: "See table below", who: "Buyer" },
];

const DUTY_GENERAL = [
  { band: "Up to $5,000", rate: "Nil" },
  { band: "$5,000 – $75,000", rate: "$1.50 per $100 over $5,000" },
  { band: "$75,000 – $540,000", rate: "$1,050 + $3.50 per $100 over $75,000" },
  { band: "$540,000 – $1,000,000", rate: "$17,325 + $4.50 per $100 over $540,000" },
  { band: "Over $1,000,000", rate: "$38,025 + $5.75 per $100 over $1,000,000" },
];

const DUTY_HOME = [
  { band: "Up to $350,000", rate: "$1.00 per $100" },
  { band: "$350,000 – $540,000", rate: "$3,500 + $3.50 per $100 over $350,000" },
  { band: "$540,000 – $1,000,000", rate: "$10,150 + $4.50 per $100 over $540,000" },
  { band: "Over $1,000,000", rate: "$30,850 + $5.75 per $100 over $1,000,000" },
];

type Region = {
  name: string;
  blurb: string;
  points: string[];
  to?: string;
  linkLabel?: string;
};

const REGIONS: Region[] = [
  {
    name: "Moreton Bay",
    blurb:
      "Redcliffe, Scarborough, Margate, North Lakes, Caboolture and the peninsula. We have an office at 15 Fortune Street, Scarborough, so there is no travel loading on a Moreton Bay file.",
    points: [
      "Same fixed fees — $990 sale, $1,499 purchase incl. GST",
      "Older peninsula homes: add a building and pest and, often, a council building records search for unapproved decks, carports and sheds",
      "North Lakes and Newport unit purchases carry body corporate search and levy adjustment costs",
      "Typical buyer disbursements: $450 – $750 before duty",
    ],
    to: "/moreton-bay",
    linkLabel: "Moreton Bay conveyancing",
  },
  {
    name: "Sunshine Coast",
    blurb:
      "Caloundra, Golden Beach, Pelican Waters, Currimundi and the wider Coast, run from our Golden Beach office at 40 Theresa Street.",
    points: [
      "Same fixed fees — no coastal premium",
      "Canal and waterfront lots at Pelican Waters and Golden Beach need revetment wall, jetty and pontoon enquiries",
      "New estates at Baringa and Aura carry infrastructure charges and developer disclosure to check",
      "Typical buyer disbursements: $450 – $800 before duty",
    ],
    to: "/conveyancing-sunshine-coast",
    linkLabel: "Sunshine Coast conveyancing",
  },
  {
    name: "Elsewhere in Queensland",
    blurb:
      "Settlement is electronic state-wide, so we act for buyers, sellers and executors outside our two office regions on the same fixed fees, entirely by phone, email and video.",
    points: [
      "Same fixed fees — no travel charge and no coastal premium",
      "Identity verified by video or through a local agent; contracts signed digitally",
      "Acreage and rural-residential blocks need water, bore, easement and vegetation checks",
      "Typical buyer disbursements: $500 – $900 before duty",
    ],
    to: "/remote-probate",
    linkLabel: "Probate anywhere in Queensland",
  },
  {
    name: "Brisbane",
    blurb:
      "Northside, inner city and the greater Brisbane region. Electronic settlement means a Brisbane purchase is handled exactly as a local one.",
    points: [
      "Same fixed fees — $990 sale, $1,499 purchase incl. GST",
      "Character and demolition control precincts: check the Brisbane City Plan overlays before you sign",
      "Apartment purchases carry body corporate, sinking fund and levy searches",
      "Higher median prices mean transfer duty, not legal fees, drives your total cost",
    ],
    to: "/conveyancing",
    linkLabel: "How our conveyancing works",
  },
];

export const Route = createFileRoute("/fees")({
  head: () => ({
    meta: [
      { title: fitTitle(`Conveyancing Fees & Costs QLD 2026 | ${FIRM.name}`) },
      {
        name: "description",
        content: fitDescription(`What conveyancing costs in Queensland: fixed fees of ${FEES.sale} for a sale and $1,499 for a purchase, plus disbursements and transfer duty tables.`),
      },
      { property: "og:title", content: fitTitle(`Conveyancing Costs in Queensland | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Fixed-fee conveyancing prices, a full disbursement breakdown and QLD transfer duty rates by region."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/fees" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/fees" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: FeesPage,
});

function FeesPage() {
  return (
    <>
      <PageHero
        image={{ src: feesHero, alt: "Calm water on a sunny day along the Sunshine Coast foreshore" }}
        eyebrow="Conveyancing costs in Queensland"
        title="Conveyancing fees and costs in Queensland"
        cta="Request a written conveyancing quote"
        lead={`A residential sale is a fixed ${FEES.sale} including GST and a residential purchase is a fixed $1,499 including GST, anywhere in Queensland. Below is the full picture — what the fee covers, what the searches cost, and how much transfer duty you will pay.`}
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-7 sm:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground/70">
              Contract review
            </p>
            <p className="mt-3 font-serif text-4xl text-foreground">{FEES.contractReview}</p>
            <p className="mt-1 text-sm text-muted-foreground">{FEES.contractReviewNote}</p>
          </div>
          <div className="rounded-sm border border-border bg-card p-7">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground/70">
              Residential sale
            </p>
            <p className="mt-3 font-serif text-4xl text-foreground">{FEES.sale}</p>
            <p className="mt-1 text-sm text-muted-foreground">{FEES.saleNote}</p>
          </div>
          <div className="rounded-sm border border-border bg-card p-7">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground/70">
              Residential purchase
            </p>
            <p className="mt-3 font-serif text-4xl text-foreground">{FEES.purchase}</p>
            <p className="mt-1 text-sm text-muted-foreground">{FEES.purchaseNote}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Fixed and confirmed in writing before you engage us — no hourly billing
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-sm border border-accent bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">{NO_CHARGE_GUARANTEE.title}</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {NO_CHARGE_GUARANTEE.long}
          </p>
          <ul className="mt-5 space-y-2 text-sm leading-relaxed text-muted-foreground">
            {NO_CHARGE_GUARANTEE.points.map((p) => (
              <li key={p} className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-6 rounded-sm border-l-2 border-accent bg-secondary/50 p-5 text-sm leading-relaxed text-muted-foreground">
          Your file is handled by a solicitor. If a title issue, finance condition or deposit dispute
          arises, you can speak directly with the person advising you. Any work outside a standard
          conveyance is discussed and quoted separately before we proceed.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">What the fixed fee includes</h2>
        <ul className="mt-6 space-y-2 text-sm leading-relaxed text-muted-foreground">
          {INCLUDED.map((i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              <span>{i}</span>
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Disbursements, itemised</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          These are paid to third parties, not to us, and we pass them on at cost. The exact set
          depends on your property. Ranges are indicative for a standard Queensland residential
          transaction.
        </p>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-3 pr-4 font-semibold text-foreground">Item</th>
                <th className="py-3 pr-4 font-semibold text-foreground">Typical cost</th>
                <th className="py-3 font-semibold text-foreground">Who pays</th>
              </tr>
            </thead>
            <tbody>
              {DISBURSEMENTS.map((d) => (
                <tr key={d.item} className="border-b border-border/60 align-top">
                  <td className="py-3 pr-4 text-muted-foreground">{d.item}</td>
                  <td className="py-3 pr-4 text-foreground">{d.cost}</td>
                  <td className="py-3 text-muted-foreground">{d.who}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Queensland transfer duty rates</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Transfer duty is paid to the Queensland Revenue Office and is almost always a buyer's
          biggest cost. Which table applies depends on whether you are buying the property as your
          home or as an investment.
        </p>

        <h3 className="mt-8 font-serif text-xl text-foreground">Investment and general rate</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[460px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-3 pr-4 font-semibold text-foreground">Purchase price</th>
                <th className="py-3 font-semibold text-foreground">Duty payable</th>
              </tr>
            </thead>
            <tbody>
              {DUTY_GENERAL.map((r) => (
                <tr key={r.band} className="border-b border-border/60">
                  <td className="py-3 pr-4 text-muted-foreground">{r.band}</td>
                  <td className="py-3 text-foreground">{r.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="mt-10 font-serif text-xl text-foreground">
          Home concession rate (you will live in it)
        </h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[460px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-3 pr-4 font-semibold text-foreground">Purchase price</th>
                <th className="py-3 font-semibold text-foreground">Duty payable</th>
              </tr>
            </thead>
            <tbody>
              {DUTY_HOME.map((r) => (
                <tr key={r.band} className="border-b border-border/60">
                  <td className="py-3 pr-4 text-muted-foreground">{r.band}</td>
                  <td className="py-3 text-foreground">{r.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
          First home buyers get a further concession on top of the home rate, and buyers of a
          newly-built first home may pay no duty at all. Foreign buyers pay an additional acquirer
          duty surcharge. Concessions and thresholds change with each state budget — we confirm your
          exact figure in writing before you sign, and you can check the current rates with the{" "}
          <a
            href="https://qro.qld.gov.au/duties/transfer-duty/"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-primary underline underline-offset-4"
          >
            Queensland Revenue Office
          </a>
          .
        </p>

        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          For a cost overview, see{" "}
          <Link to="/conveyancing-cost-qld" className="font-semibold text-primary underline underline-offset-4">conveyancing costs in Queensland</Link>.
          {" "}More detail in our guides on{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "stamp-duty-queensland" }}
            className="font-semibold text-primary underline underline-offset-4"
          >
            stamp duty in Queensland
          </Link>{" "}
          and{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "title-search-queensland" }}
            className="font-semibold text-primary underline underline-offset-4"
          >
            how to get a title search
          </Link>
          .
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Costs by region</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Our fee is the same wherever the property is. What changes region to region is the search
          set — and that is where the real cost differences sit.
        </p>
        <div className="mt-8 space-y-6">
          {REGIONS.map((r) => (
            <div key={r.name} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-2xl text-foreground">{r.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.blurb}</p>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
                {r.points.map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              {r.to ? (
                <Link
                  to={r.to}
                  className="mt-4 inline-block text-sm font-semibold text-primary underline underline-offset-4"
                >
                  {r.linkLabel}
                </Link>
              ) : null}
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Wills, estates and elder law</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            A simple will is from $660 including GST and an enduring power of attorney is
            from $440 including GST. A testamentary trust will starts at $2,200 including GST. A grant of
            probate on its own is a fixed $3,300 including GST, and full estate administration —
            including obtaining the grant of probate — is a fixed $8,800 including GST for up to
            three bank accounts, the retirement village exit or one property transfer, and
            distribution to up to six beneficiaries. More complex estates, estate
            disputes and litigation are quoted separately before we start.
          </p>
          <Link
            to="/contact"
            className="mt-5 inline-block text-sm font-semibold text-primary underline"
          >
            Ask for a quote
          </Link>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Fee questions</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
          Fees shown are current at the time of publication and apply to standard residential
          transactions. Disbursement ranges and duty rates are a guide only — duty rates and
          concessions are set by the Queensland Revenue Office and change from time to time. We will
          confirm your fee in a written costs agreement before we start work, as required under
          Queensland law.
        </p>

        <div className="mt-14">
          <EnquiryForm heading="Request a written quote" />
        </div>
      </section>

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
